import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, ShieldCheck } from 'lucide-react'
import { PhotoAvatar, type PhotoSource } from './PhotoAvatar'

/** A register row's leading circle: the person's photo when photos are on, otherwise the usual initials. */
export function RegisterAvatar({ source, fallback }: { source: PhotoSource; fallback: React.ReactNode }) {
  return source.enabled ? <PhotoAvatar source={source} size={40} /> : <>{fallback}</>
}

/**
 * "ID checked" tick for one attendee, plus gentle prompts. It only ever warns: a missing photo or an
 * unchecked box never stops the trainer marking someone present or completing the register.
 */
export function IdentityCheck({
  enabled, required, locked, hasPhoto, checkedAt, checkedBy, onToggle,
}: {
  enabled: boolean
  required: boolean
  locked: boolean
  hasPhoto: boolean
  checkedAt: string | null
  checkedBy: string | null
  onToggle: (checked: boolean) => Promise<unknown>
}) {
  const mutation = useMutation({ mutationFn: onToggle })
  if (!enabled) return null

  const checked = !!checkedAt
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
      <label className={`inline-flex items-center gap-1.5 ${locked ? 'text-gray-400' : 'text-gray-600 cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={locked || mutation.isPending}
          onChange={e => mutation.mutate(e.target.checked)}
          className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        {checked ? <><ShieldCheck size={12} className="text-green-600" /> ID checked{checkedBy ? ` by ${checkedBy}` : ''}</> : 'ID checked'}
      </label>
      {!locked && !hasPhoto && (
        <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle size={11} /> No photo on file. Tap the circle to take one.</span>
      )}
      {!locked && required && !checked && (
        <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle size={11} /> This course asks for an ID check</span>
      )}
    </div>
  )
}

/** Shown above a register when the course asks for ID checks and some are still outstanding. */
export function IdentityCheckSummary({ required, locked, outstanding }: { required: boolean; locked: boolean; outstanding: number }) {
  if (!required || locked || outstanding === 0) return null
  return (
    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5">
      <AlertTriangle size={13} className="shrink-0" />
      {outstanding} {outstanding === 1 ? 'attendee hasn’t' : 'attendees haven’t'} been ID-checked yet. You can still complete the register.
    </p>
  )
}
